import { useState } from "react";
import { Copy } from "lucide-react";

import { isRedacted } from "@/lib/utils";
import { generateKey, pemToBase64Uri } from "@/lib/keys";

export function Ed25519KeyInput({
  value,
  apiKey,
  onChange,
  onApiKeyChange,
  readOnly = false,
  showGuide = true,
}: {
  showGuide?: boolean;
  value: unknown;
  apiKey?: unknown;
  onChange?: (value: string) => void;
  onApiKeyChange?: (value: string) => void;
  readOnly?: boolean;
}) {
  const [generating, setGenerating] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const stored = typeof value === "string" ? value : "";
  const hasKey = Boolean(stored) && !isRedacted(stored);
  const storedApiKey = typeof apiKey === "string" ? apiKey : "";

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      const result = await generateKey();
      const priv = String(result?.private_key_pem || "");
      const pub = String(result?.public_key_pem || "");
      if (!priv) throw new Error("Key generation failed");
      onChange?.(pemToBase64Uri(priv));
      setPublicKey(pub);
    } catch (e: unknown) {
      setError((e instanceof Error && e.message) || "Failed to generate key");
    } finally {
      setGenerating(false);
    }
  }

  async function copyPublic() {
    if (!publicKey) return;
    await navigator.clipboard.writeText(publicKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (readOnly) {
    return <input type="password" value="••••••••" disabled />;
  }

  return (
    <div className="flex flex-col gap-3">
      {showGuide && (
      <div
        className="rounded-[10px] border border-[var(--border)] px-3 py-3 text-sm text-[var(--text-secondary)]"
        style={{ background: "var(--info-bg)" }}
      >
        <div className="mb-2 font-medium text-[var(--info)]">How to connect Binance</div>
        <ol className="m-0 list-decimal space-y-1.5 pl-4 text-[var(--text-primary)]">
          <li>Click Generate Key. Copy the public key that appears.</li>
          <li>On Binance, open API Management, create an API key, and paste that public key.</li>
          <li>
            Binance then shows an <strong>API Key</strong>. Paste it in the field below. Uploading the
            public key is not enough — Binance still needs that API Key on every request.
          </li>
        </ol>
        <p className="mb-0 mt-2 text-xs text-[var(--text-muted)]">
          The private key stays in this app and is never uploaded to Binance.
        </p>
      </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn btn-soft" disabled={generating} onClick={generate}>
          {generating ? "Generating…" : hasKey || isRedacted(stored) ? "Regenerate Key" : "Generate Key"}
        </button>
        {(hasKey || isRedacted(stored)) && !publicKey && (
          <span className="text-xs text-[var(--text-muted)]">
            A key is already stored. Generate again only if you will replace the public key on Binance.
          </span>
        )}
      </div>
      {error && <div className="text-xs text-[var(--danger)]">{error}</div>}
      {publicKey && (
        <div className="rounded-[10px] border border-[var(--border)] bg-[var(--bg-card-elevated)] p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Public key: paste it on Binance
            </span>
            <button type="button" className="btn btn-soft btn-sm" onClick={copyPublic}>
              <Copy size={14} />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-[var(--text-primary)]">
            {publicKey}
          </pre>
        </div>
      )}
      {onApiKeyChange && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
            API key from Binance <span className="text-[var(--danger)]">*</span>
          </span>
          <input
            type="text"
            className="font-mono !text-xs"
            autoComplete="off"
            value={isRedacted(storedApiKey) ? "" : storedApiKey}
            placeholder={
              isRedacted(storedApiKey)
                ? "Leave blank to keep existing"
                : "Paste the API Key shown on Binance after you add the public key"
            }
            onChange={(e) => onApiKeyChange(e.target.value)}
          />
          <span className="text-xs text-[var(--text-muted)]">
            This is the API Key string on the Binance API Management page, not the public or private key.
          </span>
        </label>
      )}
    </div>
  );
}
